import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Bluetooth, BatteryFull, BatteryLow, BatteryMedium, Cpu, Activity, ShieldCheck, RefreshCcw, Trash2, Plus, Wifi, X, Loader2, Anchor } from "lucide-react";
import { toast } from "sonner";
import PhoneFrame from "../components/PhoneFrame.jsx";
import BottomNav from "../components/BottomNav.jsx";
import api from "../lib/api.js";

const BATTERY_ICON = (pct) => (pct >= 60 ? BatteryFull : pct >= 30 ? BatteryMedium : BatteryLow);
const BATTERY_COLOR = (pct) => (pct >= 60 ? "#4ade80" : pct >= 30 ? "#FBBF24" : "#ef4444");
const SUPPORTS_WEB_BT = typeof navigator !== "undefined" && !!navigator.bluetooth;

export default function Devices() {
  const nav = useNavigate();
  const [devices, setDevices] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [pairOpen, setPairOpen] = useState(false);
  const [latest, setLatest] = useState({}); // device_id -> latest telemetry

  const load = () =>
    api.get("/devices/transformer").then((r) => {
      setDevices(r.data.devices);
      setActiveId(r.data.active_device_id);
      // fetch latest telemetry for each device
      r.data.devices.forEach((d) => {
        api.get(`/devices/transformer/${d.id}/telemetry/latest`).then((tr) => {
          setLatest((l) => ({ ...l, [d.id]: tr.data.latest }));
        }).catch(() => {});
      });
    });

  useEffect(() => { load(); }, []);

  return (
    <PhoneFrame>
      <div className="ps-aurora min-h-screen">
        <div className="px-5 pt-6 pb-28">
          <button onClick={() => nav(-1)} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="devices-back">
            <ChevronLeft size={16} /> Back
          </button>

          <div className="mt-5 ps-fade-up flex items-end justify-between">
            <div>
              <div className="ps-chip mb-3"><Bluetooth size={11} /> Wearable</div>
              <h1 className="ps-heading text-4xl font-extrabold leading-[1]">
                Back<span className="ps-serif ps-italic font-normal text-slate-500"> Transformer™</span>
              </h1>
              <p className="text-slate-400 text-sm mt-2 ps-serif ps-italic">
                Your posture, instrumented.
              </p>
            </div>
            <button
              onClick={() => setPairOpen(true)}
              className="ps-button-primary text-sm py-2 px-4 inline-flex items-center gap-1"
              data-testid="devices-pair-btn"
            >
              <Plus size={14} /> Pair
            </button>
          </div>

          {/* Empty state */}
          {devices !== null && devices.filter((d) => d.status !== "unpaired").length === 0 && (
            <div className="mt-8 ps-card p-6 text-center" data-testid="devices-empty">
              <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center"
                style={{
                  background: "linear-gradient(160deg, #4ade8055 0%, #4ade8022 40%, #4ade8010 100%)",
                  border: "1px solid #4ade8066",
                  boxShadow: "inset 0 1.5px 0 rgba(255,255,255,0.32), inset 0 -3px 6px rgba(0,0,0,0.45), 0 4px 14px -4px #4ade8077",
                }}>
                <Bluetooth size={28} color="#4ade80" strokeWidth={2.4} />
              </div>
              <div className="mt-4 ps-heading text-xl font-extrabold">No device paired</div>
              <p className="mt-2 text-sm text-slate-400 ps-serif ps-italic max-w-[260px] mx-auto leading-snug">
                Pair your Back Transformer over Bluetooth to start streaming live posture biomechanics into every walk.
              </p>
              <button onClick={() => setPairOpen(true)} className="ps-button-primary mt-5 inline-flex items-center gap-1.5" data-testid="devices-empty-cta">
                <Plus size={14} /> Pair Back Transformer
              </button>
              <div className="mt-4 text-[10px] uppercase tracking-widest text-slate-500 ps-serif ps-italic">
                {SUPPORTS_WEB_BT ? "Web Bluetooth detected" : "Web Bluetooth unavailable — guided pairing supported"}
              </div>
            </div>
          )}

          {/* Devices list */}
          <div className="mt-6 space-y-4 ps-stagger">
            {devices && devices.filter((d) => d.status !== "unpaired").map((d) => (
              <DeviceCard key={d.id} device={d} active={d.id === activeId} latest={latest[d.id]} onRefresh={load} />
            ))}
          </div>

          {/* Biomechanics primer */}
          {devices && devices.filter((d) => d.status !== "unpaired").length > 0 && (
            <div className="mt-7 ps-card p-5 relative overflow-hidden">
              <div className="absolute -top-12 -left-10 w-44 h-44 rounded-full bg-[#a78bfa]/15 blur-3xl pointer-events-none" />
              <div className="relative text-[10px] uppercase tracking-[0.25em] text-[#a78bfa] font-bold">What we measure</div>
              <div className="relative ps-heading text-xl font-extrabold mt-1">
                Seven <span className="ps-serif ps-italic font-normal text-slate-400">signals.</span>
              </div>
              <div className="relative mt-3 grid grid-cols-1 gap-2 text-[12px] text-slate-300 leading-snug">
                <Bullet color="#4ade80" label="Alignment" detail="Composite of spinal carriage + cervical neutral" />
                <Bullet color="#22d3ee" label="Shoulder retraction" detail="Degrees from forward-rounded baseline" />
                <Bullet color="#FBBF24" label="Chest expansion" detail="Thoracic opening vs slumped baseline" />
                <Bullet color="#a78bfa" label="Cervical neutral" detail="Forward-head tilt offset (0° = ideal)" />
                <Bullet color="#ec4899" label="Lateral balance" detail="L/R weight symmetry while striding" />
                <Bullet color="#f97316" label="Load distribution" detail="Backpack force routed through skeleton" />
                <Bullet color="#84cc16" label="Core engagement" detail="Deep stabilizer activation index" />
              </div>
            </div>
          )}
        </div>
      </div>
      <BottomNav />
      <PairModal open={pairOpen} onClose={() => setPairOpen(false)} onPaired={() => { setPairOpen(false); load(); }} />
    </PhoneFrame>
  );
}

function Bullet({ color, label, detail }) {
  return (
    <div className="flex items-start gap-2">
      <span className="w-1.5 h-1.5 rounded-full mt-1.5" style={{ background: color, boxShadow: `0 0 6px ${color}` }} />
      <div>
        <span className="font-bold" style={{ color }}>{label}</span>
        <span className="text-slate-400 ps-serif ps-italic ml-1">— {detail}</span>
      </div>
    </div>
  );
}

function DeviceCard({ device, active, latest, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const BIcon = BATTERY_ICON(device.battery_pct);
  const bColor = BATTERY_COLOR(device.battery_pct);

  const lastSeen = device.last_seen ? new Date(device.last_seen) : null;
  const minsAgo = lastSeen ? Math.floor((Date.now() - lastSeen.getTime()) / 60000) : null;

  const unpair = async () => {
    if (!window.confirm("Unpair this Back Transformer?")) return;
    setBusy(true);
    try {
      await api.delete(`/devices/transformer/${device.id}`);
      toast.success("Device unpaired");
      onRefresh();
    } catch (e) { toast.error("Failed to unpair"); }
    finally { setBusy(false); }
  };

  const sendDemoTelemetry = async () => {
    // Generate a realistic 12-sample batch to demonstrate the live ingestion
    setBusy(true);
    try {
      const samples = Array.from({ length: 12 }).map(() => ({
        t: new Date().toISOString(),
        alignment_score: Math.round(86 + Math.random() * 12),
        shoulder_retraction_deg: +(7 + Math.random() * 4).toFixed(2),
        chest_expansion_pct: +(12 + Math.random() * 5).toFixed(2),
        cervical_neutral_deg: +(Math.random() * 3).toFixed(2),
        lateral_balance_pct: +(95 + Math.random() * 4).toFixed(2),
        load_kg: +(3.2 + Math.random() * 0.6).toFixed(2),
        core_engagement_pct: Math.round(70 + Math.random() * 20),
      }));
      await api.post(`/devices/transformer/${device.id}/telemetry`, { samples });
      await api.post(`/devices/transformer/${device.id}/heartbeat`, {
        battery_pct: Math.max(5, device.battery_pct - 1),
      });
      toast.success("Telemetry batch ingested");
      onRefresh();
    } catch (e) { toast.error("Sync failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="ps-glass-card p-5 relative overflow-hidden" data-testid={`device-card-${device.id}`} style={{ boxShadow: `0 1px 0 0 rgba(255,255,255,0.08) inset, 0 -1px 0 0 rgba(0,0,0,0.25) inset, 0 22px 50px -24px rgba(74,222,128,0.55)` }}>
      <div aria-hidden className="ps-mesh-drift absolute inset-[-30%] opacity-50 pointer-events-none" style={{ background: "radial-gradient(circle at 25% 20%, #4ade8044 0%, transparent 45%), radial-gradient(circle at 80% 80%, #22d3ee25 0%, transparent 50%)", filter: "blur(20px)" }} />

      <div className="relative flex items-start gap-3">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 relative"
          style={{
            background: "linear-gradient(160deg, #4ade8055 0%, #4ade8022 40%, #4ade8010 100%)",
            border: "1px solid #4ade8066",
            boxShadow: "inset 0 1.5px 0 rgba(255,255,255,0.32), inset 0 -3px 6px rgba(0,0,0,0.45), 0 4px 14px -4px #4ade8077",
          }}
        >
          <Cpu size={24} color="#4ade80" strokeWidth={2.4} />
          <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-[#050E15]" style={{ background: active ? "#4ade80" : "#64748b", boxShadow: active ? "0 0 8px #4ade80" : "none" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="ps-heading font-extrabold text-lg leading-tight">{device.name}</div>
          <div className="text-[11px] text-slate-400 ps-serif ps-italic">
            {device.model} · S/N {device.serial}
          </div>
          <div className="mt-1 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold" style={{ color: active ? "#4ade80" : "#94a3b8" }}>
            <Wifi size={11} /> {active ? "Connected" : device.status}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="relative mt-4 grid grid-cols-3 gap-2">
        <StatBox label="Battery" accent={bColor} icon={BIcon} value={`${device.battery_pct}%`} />
        <StatBox label="Firmware" accent="#a78bfa" icon={ShieldCheck} value={device.firmware_version} />
        <StatBox label="Last seen" accent="#22d3ee" icon={Activity} value={minsAgo === null ? "—" : minsAgo < 1 ? "Just now" : minsAgo < 60 ? `${minsAgo}m` : `${Math.floor(minsAgo / 60)}h`} />
      </div>

      {/* Live telemetry */}
      {latest && latest.summary && (
        <div className="relative mt-4 rounded-2xl border border-[#4ade80]/30 bg-[#4ade80]/05 p-3" data-testid="device-telemetry">
          <div className="text-[9px] uppercase tracking-widest font-bold text-[#4ade80] flex items-center gap-1.5">
            <Anchor size={10} /> Latest batch ({latest.sample_count} samples)
          </div>
          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
            <TBio label="Alignment" value={`${latest.summary.alignment_score}`} unit="/100" />
            <TBio label="Shoulder retraction" value={`${latest.summary.shoulder_retraction_deg}°`} />
            <TBio label="Chest expansion" value={`${latest.summary.chest_expansion_pct}%`} />
            <TBio label="Cervical neutral" value={`${latest.summary.cervical_neutral_deg}°`} />
            <TBio label="Lateral balance" value={`${latest.summary.lateral_balance_pct}%`} />
            <TBio label="Core engagement" value={`${latest.summary.core_engagement_pct}%`} />
          </div>
        </div>
      )}

      <div className="relative mt-4 flex gap-2">
        <button
          onClick={sendDemoTelemetry}
          disabled={busy}
          data-testid={`device-sync-${device.id}`}
          className="flex-1 ps-button-secondary text-xs py-2 inline-flex items-center justify-center gap-1.5"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCcw size={13} />} Pull latest
        </button>
        <button
          onClick={unpair}
          disabled={busy}
          data-testid={`device-unpair-${device.id}`}
          className="px-3 py-2 rounded-full bg-[#15303E] border border-[#ef4444]/40 text-[#ef4444] text-xs font-bold inline-flex items-center gap-1"
        >
          <Trash2 size={12} /> Unpair
        </button>
      </div>
    </div>
  );
}

function StatBox({ label, accent, icon: Icon, value }) {
  return (
    <div className="rounded-2xl border p-2.5 text-center" style={{ borderColor: `${accent}40`, background: `${accent}0d` }}>
      <Icon size={14} color={accent} className="mx-auto" />
      <div className="ps-heading text-[13px] font-extrabold mt-1 ps-tabular">{value}</div>
      <div className="text-[8px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
    </div>
  );
}

function TBio({ label, value, unit }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-slate-400 ps-serif ps-italic">{label}</span>
      <span className="font-bold ps-tabular text-slate-200">{value}<span className="text-slate-500 text-[10px] ml-0.5">{unit || ""}</span></span>
    </div>
  );
}

/* ---------- Pairing modal ---------- */
function PairModal({ open, onClose, onPaired }) {
  const [stage, setStage] = useState("scan"); // scan -> connecting -> success
  const [name, setName] = useState("");
  const [serial, setSerial] = useState("");
  const cancelRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setStage("scan");
    setName("");
    setSerial("");
    cancelRef.current = false;
  }, [open]);

  if (!open) return null;

  const scanAndPair = async () => {
    cancelRef.current = false;
    setStage("connecting");
    // Try Web Bluetooth first (real device flow), fall back to simulated demo
    let detectedName = "";
    let detectedSerial = "";
    if (SUPPORTS_WEB_BT) {
      try {
        const dev = await navigator.bluetooth.requestDevice({
          filters: [{ namePrefix: "Posture" }, { namePrefix: "BackTransformer" }, { namePrefix: "BT" }],
          optionalServices: ["battery_service", "device_information"],
        });
        detectedName = dev.name || "Back Transformer";
        detectedSerial = (dev.id || "").toUpperCase().slice(0, 12) || `BT-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
      } catch (e) {
        // user cancelled or BT scan failed — fall through to simulated
      }
    }
    if (!detectedSerial) {
      // Simulated handshake — 1.6s "connecting" delay for the UX
      await new Promise((r) => setTimeout(r, 1600));
      if (cancelRef.current) { setStage("scan"); return; }
      detectedName = "My Back Transformer";
      detectedSerial = `BT-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
    }
    try {
      await api.post("/devices/transformer/pair", {
        name: detectedName,
        serial: detectedSerial,
        model: "Back Transformer v2",
        firmware_version: "1.4.2",
        battery_pct: 87,
      });
      setName(detectedName);
      setSerial(detectedSerial);
      setStage("success");
      toast.success("Back Transformer paired");
      setTimeout(() => onPaired(), 1400);
    } catch (e) {
      toast.error("Pairing failed");
      setStage("scan");
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm ps-fade-up" data-testid="pair-modal">
      <div className="ps-card-elev p-6 w-full max-w-sm relative overflow-hidden">
        <button onClick={() => { cancelRef.current = true; onClose(); }} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/5">
          <X size={18} className="text-slate-400" />
        </button>
        {stage === "scan" && (
          <>
            <div className="ps-chip mb-2"><Bluetooth size={11} /> Bluetooth</div>
            <h2 className="ps-heading text-2xl font-extrabold">
              Pair your <span className="ps-serif ps-italic font-normal">Back Transformer.</span>
            </h2>
            <p className="text-xs text-slate-400 mt-2 ps-serif ps-italic leading-snug">
              Power the device on, hold the side button for 3 seconds until the green light pulses, then tap below.
            </p>
            <button onClick={scanAndPair} className="ps-button-primary w-full mt-5 inline-flex items-center justify-center gap-2" data-testid="pair-scan-btn">
              <Bluetooth size={16} /> Scan
            </button>
            <div className="mt-3 text-center text-[10px] uppercase tracking-widest text-slate-500">
              {SUPPORTS_WEB_BT ? "Live scan via Web Bluetooth" : "Guided handshake — Web Bluetooth unavailable in this browser"}
            </div>
          </>
        )}
        {stage === "connecting" && (
          <div className="text-center py-6" data-testid="pair-connecting">
            <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center ps-pulse" style={{ background: "radial-gradient(circle, #4ade80 0%, #4ade8033 60%, transparent 100%)" }}>
              <Bluetooth size={36} color="#022014" />
            </div>
            <div className="ps-heading text-xl font-extrabold mt-5">Connecting…</div>
            <p className="text-xs text-slate-400 ps-serif ps-italic mt-2">Negotiating GATT services + handshake.</p>
          </div>
        )}
        {stage === "success" && (
          <div className="text-center py-6" data-testid="pair-success">
            <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center" style={{ background: "linear-gradient(160deg, #4ade8055, #4ade80aa)", boxShadow: "0 18px 50px -10px #4ade80aa" }}>
              <ShieldCheck size={36} color="#022014" />
            </div>
            <div className="ps-heading text-xl font-extrabold mt-5">Paired!</div>
            <p className="text-xs text-slate-300 ps-serif ps-italic mt-2">{name} · S/N {serial}</p>
            <p className="text-[10px] text-slate-500 ps-serif ps-italic mt-1">Telemetry will now stream into every walk session.</p>
          </div>
        )}
      </div>
    </div>
  );
}
